<template>
  <div class="w-full flex justify-center">
    <jade-page-container size="wide">
      <onboarding-loading v-if="isLoading" />
      <div
        class="w-full flex flex-col gap-6"
        v-else
      >
        <kyc-feedback v-if="canShowKycFeedback"></kyc-feedback>
        <div
          class="w-full flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4"
        >
          <div class="flex sm:items-center gap-4 flex-col sm:flex-row">
            <date-select
              :is-loading="isLoading"
              class="w-full lg:w-auto"
            />
            <report-button
              size="medium"
              :report="report"
              @callback="printDashboard"
              class="w-full lg:w-auto"
            />
          </div>
          <div class="flex sm:items-center gap-4 flex-col sm:flex-row">
            <jade-button
              v-if="!isBusinessGateway"
              icon="percent-box"
              size="medium"
              variant="neutral-ghost"
              @click="toggleDrawerFeesCalculator"
              class="w-full lg:w-auto"
            >
              {{ $t('simulator') }}
            </jade-button>

            <jade-button
              v-if="canShowShop"
              size="medium"
              icon="plus"
              variant="primary-solid"
              @click="openShop"
              class="w-full lg:w-auto"
              >{{ $t('create-order') }}</jade-button
            >
          </div>
        </div>

        <section
          ref="dashboard"
          class="w-full flex flex-col gap-6"
        >
          <charges :data="charges" />
          <transaction-status
            :data="status"
            :total="totalQuantity"
          />
          <div class="flex flex-col lg:flex-row gap-6">
            <payment-methods :data="paymentMethods" />
            <brands :data="brands" />
          </div>
          <div class="flex flex-col lg:flex-row gap-6">
            <acquirer
              v-if="isBusinessGateway"
              :data="acquirers"
            />
            <refusal-codes
              :data="refusalCodes"
              :total="totalQuantity"
            />
          </div>
          <div class="flex flex-col lg:flex-row gap-6">
            <conversion :data="paymentMethods" />
            <installments :data="installments" />
          </div>
        </section>
      </div>
    </jade-page-container>
  </div>
</template>

<script>
import { JadePageContainer, JadeButton } from '@jade'
import { mapGetters, mapActions } from 'vuex'

import ReportButton from '@/components/project/molecules/ReportButton.vue'

import MathManager from '@/utils/math-manager.utils'
import printViewByRef from '@/utils/print-view-by-ref.utils'
import { isGateway } from '@/enums/business-model.enum'
import shopEnabled from '@/actions-permission/shop.permissions'
import { kycFeedbackBannerEnabled } from '@/actions-permission/kyc.permissions'
import { EXTERNAL_ENDPOINTS } from '@/external-endpoints'
import Amplitude from '@/amplitude-events/resume.amplitude'
import '@pagarme/kyc-feedback/web-comp'

export default {
  name: 'Home',
  components: {
    ReportButton,
    OnboardingLoading: () => import('./components/OnboardingLoading.vue'),
    DateSelect: () => import('./components/DateSelect.vue'),
    TransactionStatus: () => import('./TransactionStatus'),
    PaymentMethods: () => import('./PaymentMethods'),
    Acquirer: () => import('./Acquirer'),
    Brands: () => import('./Brands'),
    RefusalCodes: () => import('./RefusalCodes'),
    Installments: () => import('./Installments'),
    Conversion: () => import('./Conversion'),
    Charges: () => import('./Charges'),
    JadePageContainer,
    JadeButton
  },
  data () {
    return {
      report: {
        loading: false
      }
    }
  },
  computed: {
    ...mapGetters({
      data: 'HOME_DATA',
      isLoading: 'HOME_DATA_IS_LOADING',
      account: 'account',
      hasShopUnavailable: 'hasShopUnavailable',
      permissionIsEnabled: 'permissionIsEnabled',
      componentIsEnabled: 'componentIsEnabled',
      merchantId: 'merchantId',
      accountId: 'accountId',
      showFeeSimulator: 'GET_DRAWER_FEES_CALCULATOR',
      isTestAccount: 'isTestAccount',
      userType: 'userType'
    }),
    isBusinessGateway () {
      return isGateway(this.account?.business_model)
    },
    totalQuantity () {
      return this.data.quantity
    },
    buckets () {
      return this.data?.buckets ?? {}
    },
    status () {
      return this.buckets.status
    },
    paymentMethods () {
      return this.buckets.payment_method
    },
    brands () {
      return this.buckets.brands.filter(item => item.authorized_tpv !== 0)
    },
    acquirers () {
      return this.buckets.acquirers.filter(item => item.authorized_tpv !== 0)
    },
    installments () {
      return this.buckets.installments
    },
    refusalCodes () {
      return (
        this.status.find(item => item.name === 'failed')?.buckets?.acquirers ??
        []
      )
    },
    charges () {
      return {
        tpvLabel: MathManager.convertToMonetary(this.data?.tpv),
        authorizedTpvLabel: MathManager.convertToMonetary(
          this.data?.authorized_tpv
        ),
        ticketLabel: MathManager.convertToMonetary(
          this.data.average_ticket_authorized
        ),
        salesLabel: this.totalQuantity,
        data: this.buckets.time_series
      }
    },
    containerStyled () {
      return {
        'dashboard-container': true,
        'dashboard-container__double': this.isBusinessGateway
      }
    },
    canShowShop () {
      return shopEnabled(
        this.permissionIsEnabled,
        this.componentIsEnabled,
        this.hasShopUnavailable
      )
    },
    canShowKycFeedback () {
      return kycFeedbackBannerEnabled(
        this.userType.key,
        this.account.business_model,
        this.isTestAccount
      )
    },
    urlShop () {
      return EXTERNAL_ENDPOINTS.SHOP.url(this.merchantId, this.accountId)
    }
  },

  mounted () {
    this.$nextTick(() => Amplitude.events.view.loadView(this.$route.query))
  },

  methods: {
    ...mapActions(['TOGGLE_DRAWER_FEES_CALCULATOR']),
    toggleDrawerFeesCalculator () {
      this.TOGGLE_DRAWER_FEES_CALCULATOR()
      Amplitude.events.actions.simulatorClicked({
        open: this.showFeeSimulator
      })
    },
    printDashboard () {
      const { date_filters } = this.$route.query
      const refDashboard = this.$refs.dashboard

      Amplitude.events.actions.exportDataClicked(date_filters)

      printViewByRef(refDashboard, date_filters)
    },
    openShop () {
      Amplitude.events.actions.shopClicked()
      window.open(this.$sanitize(this.urlShop), '_blank')
    }
  }
}
</script>
<i18n>
  {
    "pt-BR": {
      "create-order": "Criar pedido",
      "simulator": "Simulador de vendas"
    },
    "en-US": {
      "create-order": "Create order",
      "simulator": "Sales simulator"
    }
  }
</i18n>
